import { components } from 'react-select';
import { Form, Formik, useFormikContext } from 'formik';

import { useGroups } from '@/react/portainer/environments/environment-groups/queries';
import { TypedPolicy } from '@/react/portainer/policies/types';
import { notifySuccess } from '@/portainer/services/notifications';
import { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';

import { PortainerSelect, Option } from '@@/form-components/PortainerSelect';
import { FormControl } from '@@/form-components/FormControl';
import { Button, LoadingButton } from '@@/buttons';

import { useUpdatePolicyMutation } from '../../queries/useUpdatePolicyMutation';
import { CreatePolicyData } from '../../queries/types';
import {
  EnvironmentGroupOptionComponent,
  getFilteredEnvironmentGroupOptions,
  hasSelectedGroupsWithConflicts,
} from '../../components/PolicyConfigurationFormSection';
import { confirmPolicyWithPreview } from '../../components/confirmPolicyWithPreview';

interface FormValues {
  environmentGroups: Array<number>;
}

interface Props {
  policy: TypedPolicy<unknown> | undefined;
  groups: Array<EnvironmentGroup> | undefined;
  updateMutation: ReturnType<typeof useUpdatePolicyMutation>;
}

export function EnvironmentGroupsSelector({
  policy,
  groups,
  updateMutation,
}: Props) {
  const groupOptionsQuery = useGroups({
    select: (environmentGroups) =>
      policy
        ? getFilteredEnvironmentGroupOptions(
            policy.Type,
            environmentGroups,
            policy.Id
          )
        : [],
  });

  const initialValues: FormValues = {
    environmentGroups: policy?.EnvironmentGroups || [],
  };

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      onSubmit={handleApply}
      enableReinitialize
    >
      <Form className="form-horizontal">
        <EnvironmentGroupsSelectorInner
          groupOptions={groupOptionsQuery.data || []}
          groupOptionsLoading={groupOptionsQuery.isLoading}
          isSubmitting={updateMutation.isLoading}
        />
      </Form>
    </Formik>
  );

  async function handleApply(values: FormValues) {
    if (!policy || !groups) return;

    const originalGroups = policy.EnvironmentGroups || [];

    // Show confirmation modal with impact preview
    const confirmed = await confirmPolicyWithPreview({
      policyId: policy.Id,
      policyName: policy.Name,
      policyType: policy.Type,
      environmentGroupIds: values.environmentGroups,
      originalEnvironmentGroupIds: originalGroups,
      groups,
    });

    if (!confirmed) return;

    const needsOverride = hasSelectedGroupsWithConflicts(
      values.environmentGroups,
      groups,
      policy.Type
    );

    try {
      await updateMutation.mutateAsync({
        id: policy.Id,
        payload: {
          Name: policy.Name,
          Type: policy.Type,
          EnvironmentGroups: values.environmentGroups,
          Data: policy.Data as CreatePolicyData,
          AllowOverride: needsOverride,
        },
      });
      notifySuccess('Success', 'Policy environment groups updated');
    } catch {
      // Error handled globally
    }
  }
}

interface EnvironmentGroupsSelectorInnerProps {
  groupOptions: Array<Option<number>>;
  groupOptionsLoading: boolean;
  isSubmitting: boolean;
}

function EnvironmentGroupsSelectorInner({
  groupOptions,
  groupOptionsLoading,
  isSubmitting,
}: EnvironmentGroupsSelectorInnerProps) {
  const { values, setFieldValue, dirty, resetForm, submitForm } =
    useFormikContext<FormValues>();

  return (
    <FormControl
      label="Environment groups"
      tooltip="Select one or more environment groups to apply this policy to"
    >
      <div className="flex items-center gap-2">
        <div className="flex-grow">
          <PortainerSelect<number>
            isMulti
            value={values.environmentGroups}
            onChange={(newGroups) =>
              setFieldValue('environmentGroups', newGroups)
            }
            options={groupOptions}
            isLoading={groupOptionsLoading}
            disabled={isSubmitting}
            data-cy="policy-groups-select"
            noOptionsMessage={() => 'No environment groups available.'}
            components={{
              Option:
                EnvironmentGroupOptionComponent as typeof components.Option,
            }}
          />
        </div>
        {dirty && (
          <div className="flex gap-2 flex-shrink-0">
            <LoadingButton
              type="button"
              color="primary"
              size="medium"
              onClick={submitForm}
              disabled={isSubmitting}
              isLoading={isSubmitting}
              loadingText="Applying changes"
              data-cy="apply-group-changes"
            >
              Apply changes
            </LoadingButton>
            <Button
              color="default"
              size="medium"
              onClick={() => resetForm()}
              disabled={isSubmitting}
              data-cy="cancel-group-changes"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </FormControl>
  );
}
